import { useMemo } from 'react';
import { CustomPagePhotoPresentationInfoFragment, EventPageType, PhotoAlignment, PhotoFragment, WebsitePagePhotoPresentationFragment } from '@graphql/generated';

interface Page {
  id: string;
  type: EventPageType;
  photo?: PhotoFragment | null;
  photoPresentation?: WebsitePagePhotoPresentationFragment | null;
}

type Pages = ReadonlyArray<Page>;

interface PageContainer
  extends Readonly<{
    id: string;
    mainPhotoAlignment?: PhotoAlignment | null;
    mainPhotoPresentationLayout?: CustomPagePhotoPresentationInfoFragment['mainPhotoPresentationLayout'];
    mainPhotoCropInfo?: CustomPagePhotoPresentationInfoFragment['mainPhotoCropInfo'];
  }> {}

type PageContainers = ReadonlyArray<PageContainer>;

/**
 * Used to map page photo alignment from page container
 * Custom Pages photo focus alignment and photo are stored with different services
 */
export function usePagesWithPhotoAlignment<P extends Pages, C extends PageContainers>(pages?: P | null, pageContainers?: C | null) {
  const photoAlignedPages = useMemo(() => {
    if (!pages || !pageContainers) return pages ?? [];

    return pages.map(page => {
      const customPage = pageContainers.find(container => container.id === page.id);
      const customPagePhotoAlignment = customPage?.mainPhotoAlignment;
      const customPagePhotoPresentation = customPage?.mainPhotoPresentationLayout;
      const customPagePhotoCropInfo = customPage?.mainPhotoCropInfo;

      if (page.type === EventPageType.custom) {
        if (customPagePhotoAlignment && page.photo) {
          const photo = {
            ...page.photo,
            layout: {
              ...(page.photo.layout || {}),
              alignX: customPagePhotoAlignment.x,
              alignY: customPagePhotoAlignment.y,
            },
          };
          page = { ...page, photo };
        }

        if (customPagePhotoPresentation && customPagePhotoCropInfo) {
          page = {
            ...page,
            photoPresentation: {
              id: customPage.id,
              presentationLayout: customPagePhotoPresentation,
              cropInfo: customPagePhotoCropInfo,
            } as WebsitePagePhotoPresentationFragment,
          };
        }
      }
      return page;
    }) as unknown as P;
  }, [pages, pageContainers]);

  return photoAlignedPages;
}
